import React, { useEffect, useState } from 'react';
import './Signup.scss';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { loginWithGoogle } from '../services/auth';
import { FirebaseError } from 'firebase/app';

// Map Firebase error codes -> friendly FR messages
const friendlyAuthError = (code?: string): string => {
  const map: Record<string, string> = {
    'auth/email-already-in-use': 'Cet e-mail est déjà utilisé.',
    'auth/invalid-email': 'Adresse e-mail invalide.',
    'auth/weak-password': 'Mot de passe trop court.',
    'auth/operation-not-allowed': 'Méthode e-mail/mot de passe désactivée.',
    'auth/network-request-failed': 'Problème réseau. Réessayez.',
    'auth/popup-closed-by-user': 'Fenêtre fermée.',
    'auth/cancelled-popup-request': 'Fenêtre déjà ouverte.',
    'auth/popup-blocked': 'Fenêtre bloquée par le navigateur.',
  };
  return map[code || ''] || 'Impossible de créer le compte. Réessayez.';
};

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Auto-hide errors after 6s
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setError('');

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    try {
      setLoading(true);
      await createUserWithEmailAndPassword(auth, email, password);
      window.location.href = '/dashboard';
    } catch (err) {
      const fe = err as FirebaseError;
      console.error('Firebase signUp error:', fe.code, fe.message);
      setError(friendlyAuthError(fe.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      await loginWithGoogle();
      window.location.href = '/dashboard';
    } catch (err) {
      const fe = err as FirebaseError;
      console.error('Firebase Google signUp error:', fe.code, fe.message);
      setError(friendlyAuthError(fe.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <div className="signupPage">
        <div className="topSection">
          <div className="card">
            <h2 className="title">Créer un compte</h2>

            <form className="form" onSubmit={handleSubmit} noValidate>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="input"
                required
                autoComplete="email"
              />

              {/* Password with eye icon */}
              <div className="inputWrap">
                <input
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Mot de passe"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="input"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggleBtn"
                  aria-label={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  onClick={() => setShowPwd(s => !s)}
                >
                  {showPwd ? (
                    // eye-off
                    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M2 2l20 20" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <path d="M1.5 12S4.5 5 12 5 22.5 12 22.5 12 20.8 15.9 17 18.3" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <circle cx="12" cy="12" r="3.5" fill="none" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  ) : (
                    // eye
                    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M1.5 12S4.5 5 12 5s10.5 7 10.5 7-3 7-10.5 7S1.5 12 1.5 12z" fill="none" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="12" r="3.5" fill="none" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                </button>
              </div>

              {/* Confirm password with eye icon */}
              <div className="inputWrap">
                <input
                  type={showConfirmPwd ? 'text' : 'password'}
                  placeholder="Confirmer le mot de passe"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="input"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggleBtn"
                  aria-label={showConfirmPwd ? 'Masquer la confirmation' : 'Afficher la confirmation'}
                  onClick={() => setShowConfirmPwd(s => !s)}
                >
                  {showConfirmPwd ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M2 2l20 20" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <path d="M1.5 12S4.5 5 12 5 22.5 12 22.5 12 20.8 15.9 17 18.3" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <circle cx="12" cy="12" r="3.5" fill="none" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M1.5 12S4.5 5 12 5s10.5 7 10.5 7-3 7-10.5 7S1.5 12 1.5 12z" fill="none" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="12" r="3.5" fill="none" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                </button>
              </div>

              <button type="submit" className="submitBtn" disabled={loading}>
                {loading ? 'Création…' : 'Créer un compte'}
              </button>

              {error && <div className="error">{error}</div>}

              <button
                type="button"
                onClick={handleGoogle}
                className="googleBtn"
                disabled={loading}
                aria-label="S'inscrire avec Google"
              >
                <svg width="20" height="20" viewBox="0 0 48 48" style={{ marginRight: 8 }}>
                  <g>
                    <path fill="#4285F4" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.23l6.85-6.85C35.64 2.09 30.13 0 24 0 14.82 0 6.73 5.48 2.69 13.44l7.98 6.2C12.13 13.13 17.62 9.5 24 9.5z"/>
                    <path fill="#34A853" d="M46.1 24.55c0-1.64-.15-3.22-.42-4.74H24v9.01h12.42c-.54 2.9-2.18 5.36-4.65 7.01l7.19 5.6C43.93 37.36 46.1 31.44 46.1 24.55z"/>
                    <path fill="#FBBC05" d="M10.67 28.09c-1.01-2.9-1.01-6.08 0-8.98l-7.98-6.2C.64 16.36 0 20.09 0 24s.64 7.64 2.69 10.09l7.98-6.2z"/>
                    <path fill="#EA4335" d="M24 48c6.13 0 11.64-2.03 15.53-5.53l-7.19-5.6c-2.01 1.35-4.59 2.13-8.34 2.13-6.38 0-11.87-3.63-14.33-8.93l-7.98 6.2C6.73 42.52 14.82 48 24 48z"/>
                    <path fill="none" d="M0 0h48v48H0z"/>
                  </g>
                </svg>
                S'inscrire avec Google
              </button>
            </form>

            <div className="loginLink">
              Déjà un compte ? <a href="/login" className="loginAnchor">Connexion</a>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Signup;
